/** AI changes (backend metaplan A3c, AIE §10) — `console_ai_changes_view`; reverting needs
 *  `console_ai_changes_revert`. Every change set the change engine writes — an MCP or
 *  console-agent apply, a Design Import apply, a translation sync, or a revert of another
 *  change set — is an audited, revertible unit:
 *
 *  - `GET /ai_changes/` — the grid: one row per change set, newest first (or by records /
 *    cost), limit/offset, the console count modes and the extended statement budget.
 *  - `GET /ai_changes/{id}/` — attribution (who / how / when, MCP client and grant, IPs),
 *    the proposal it applied, revert links, `revertable` and what the viewer may do.
 *  - `GET /ai_changes/{id}/entries/` and `…/entries/{seq}/` — the records it touched, and one
 *    record's before → after. The snapshot files live in a private store the console never
 *    links to: every diff comes through `entry()`.
 *  - `GET /ai_changes/{id}/history/` — the change-history rows it wrote (its correlation id).
 *  - `GET /ai_changes/{id}/revert_plan/` — the revert's dry run; `POST …/revert/` runs it
 *    against the digest of the plan the operator decided on.
 *
 *  Shapes come from the generated spec types; this module adds the query keys and the words. */
import { apiGet, apiPost, extendedListConfig } from './client';
import type { CountMode, CountOnlyResult } from './countStatus';
import type { components } from './types';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

type Schemas = components['schemas'];

export type AIChangeChannel = Schemas['AIChangeChannel'];
export type AIChangeStatus = Schemas['AIChangeStatus'];
export type AIChangeRef = Schemas['AIChangeRef'];
export type AIChangeActor = Schemas['AIChangeActor'];
export type AIChangeListItem = Schemas['AIChangeListItem'];
export type AIChangeDetail = Schemas['AIChangeDetail'];
export type AIChangeProposal = Schemas['AIChangeProposal'];
export type AIChangeEntry = Schemas['AIChangeEntry'];
export type AIChangeEntryList = Schemas['AIChangeEntryList'];
export type AIChangeEntryDiff = Schemas['AIChangeEntryDiff'];
export type AIChangeHistoryRow = Schemas['AIChangeHistoryRow'];
export type AIChangeHistory = Schemas['AIChangeHistory'];
export type AIChangeRevertHistoryRow = Schemas['AIChangeRevertHistoryRow'];
export type AIChangeRevertIssue = Schemas['AIChangeRevertIssue'];
export type AIChangeRevertEntry = Schemas['AIChangeRevertEntry'];
export type AIChangeRevertPlan = Schemas['AIChangeRevertPlan'];
export type AIChangeRevertRequest = Schemas['AIChangeRevertRequest'];
export type AIChangeRevertOutcome = Schemas['AIChangeRevertOutcome'];
export type AIChangeRevertOutcomeStatus = AIChangeRevertOutcome['status'];
export type AIChangeRevertError = Schemas['AIChangeRevertError'];
export type AIChangeRevertErrorCode = AIChangeRevertError['code'];

// ---- list ------------------------------------------------------------------

/** The list filters, by their API names (the grid's `useGridFilters` defs resolve to these). */
export interface AIChangeFilters {
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  /** Acting user id. */
  actor?: number;
  /** Acting user's email, contains (kept after the user is gone). */
  actor_email?: string;
  /** One channel or a comma-separated list. */
  channel?: string;
  /** One status or a comma-separated list. */
  status?: string;
  /** OAuth client id (exact) or name (contains). */
  client?: string;
  created_after?: string;
  created_before?: string;
  /** A record it changed was changed again since the apply. */
  has_conflicts?: boolean;
  /** The reverts of one change set. */
  revert_of?: string;
  /** One content apply's change sets, one per record (client MCP plan §4.7 "Batches"). */
  batch_id?: string;
  /** A change set id (exact), or text in its reason / summary. */
  search?: string;
}

/** Everything `list()` and `count()` share — build both from ONE helper (the count-mode rule),
 *  or the total can describe a different set than the rows. */
export interface AIChangeQuery extends AIChangeFilters {
  ordering?: string;
  timeout?: 'extended';
}

export interface AIChangeListParams extends AIChangeQuery {
  limit?: number;
  offset?: number;
  /** Only when the operator overrode the grid's count mode; the default lives on the view. */
  count_mode?: CountMode;
}

type QueryValue = string | number | boolean | null | undefined;

function toQuery(params: Record<string, QueryValue>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    usp.set(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

const path = (id: string) => `/ai_changes/${encodeURIComponent(id)}/`;

export function list(
  params: AIChangeListParams = {},
): Promise<PaginatedResponse<AIChangeListItem>> {
  return apiGet(`/ai_changes/${toQuery({ ...params })}`, extendedListConfig(params.timeout));
}

/** The exact total of the same query, computed off-request (`count_only=1&count_mode=async`). */
export function count(params: AIChangeQuery = {}): Promise<CountOnlyResult> {
  return apiGet(
    `/ai_changes/${toQuery({ ...params, count_only: 1, count_mode: 'async' })}`,
    extendedListConfig(params.timeout),
  );
}

// ---- one change set ----------------------------------------------------------

export function retrieve(id: string): Promise<AIChangeDetail> {
  return apiGet(path(id));
}

export function entries(id: string): Promise<AIChangeEntryList> {
  return apiGet(`${path(id)}entries/`);
}

export function entry(id: string, seq: number): Promise<AIChangeEntryDiff> {
  return apiGet(`${path(id)}entries/${seq}/`);
}

export function history(id: string): Promise<AIChangeHistory> {
  return apiGet(`${path(id)}history/`);
}

// ---- revert ------------------------------------------------------------------

/** A plan reads up to hundreds of snapshot files: give it longer than the client's default
 *  30 s, still well under the ALB's kill line. (The revert itself runs as a background task.) */
export const REVERT_HTTP_TIMEOUT_MS = 120_000;

/** What the operator decided so far: the records to skip, force over conflicts, and the
 *  soft-reference confirmation. Each combination is its own plan (and its own digest). */
export interface RevertDecisions {
  skips: readonly number[];
  force: boolean;
  confirmSoftRefs: boolean;
}

/** Skips in one canonical order, so `[7, 3]` and `[3, 7]` are the same plan. */
export function normalizeSkips(skips: readonly number[]): number[] {
  return [...new Set(skips)].sort((a, b) => a - b);
}

export function revertPlan(id: string, decisions: RevertDecisions): Promise<AIChangeRevertPlan> {
  const skips = normalizeSkips(decisions.skips);
  const qs = toQuery({
    skips: skips.length ? skips.join(',') : undefined,
    force: decisions.force || undefined,
    confirm_soft_refs: decisions.confirmSoftRefs || undefined,
  });
  return apiGet(`${path(id)}revert_plan/${qs}`, { timeout: REVERT_HTTP_TIMEOUT_MS });
}

/** Starts the revert as a background task: 202 → `{task_id, status: 'pending'}`; poll
 *  `revertOutcome` for what happened. Refused before it starts: 409 `revert_refused` /
 *  `change_sets_disabled` as a `ConsoleApiError` whose body `revertErrorOf` reads (also the
 *  plan's 409). */
export function revert(id: string, body: AIChangeRevertRequest): Promise<AIChangeRevertOutcome> {
  return apiPost(`${path(id)}revert/`, body);
}

/** The outcome of a started revert — `pending` while its task runs, then what really
 *  happened (read from the run's audit row and the task record, never guessed). */
export function revertOutcome(id: string, taskId: string): Promise<AIChangeRevertOutcome> {
  return apiGet(`${path(id)}revert/${encodeURIComponent(taskId)}/`);
}

const REVERT_ERROR_CODES: readonly AIChangeRevertErrorCode[] = [
  'revert_refused',
  'change_sets_disabled',
];

/** The typed body of a refusal before anything ran (the plan's or the revert's 409), or null
 *  for any other failure. */
export function revertErrorOf(body: unknown): AIChangeRevertError | null {
  const code = (body as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' && (REVERT_ERROR_CODES as readonly string[]).includes(code)
    ? (body as AIChangeRevertError)
    : null;
}

/** The domains whose change sets ops revert only with the console permission of each kind of
 *  record they change, on top of the revert permission (backend `ai_changes.kind_refusal`, client
 *  MCP WPS P3-16). The detail's `permissions` already say it; these name why. */
const KIND_PERMISSION_DOMAINS: readonly string[] = ['content', 'vocabulary'];

export const needsKindPermissions = (domain: string) => KIND_PERMISSION_DOMAINS.includes(domain);

export const REVERT_KIND_PERMISSION_TEXT =
  'Reverting this change set also needs the console permission of each kind of record it changes (console_regions_manage for regions, console_ai_vocabulary_manage for the AI vocabulary, …).';

export const FORCE_KIND_PERMISSION_TEXT =
  'Overwriting the conflicts (force) needs console_ai_changes_revert and the console permission of each kind of record it changes.';

/** The backend's words for a revert refused for a kind's permission (403 `permission_required`),
 *  or null for any other body. */
export function permissionRequiredOf(body: unknown): string | null {
  const b = body as { code?: unknown; detail?: unknown } | null | undefined;
  return b?.code === 'permission_required' && typeof b.detail === 'string' && b.detail
    ? b.detail
    : null;
}

// ---- query keys ----------------------------------------------------------------

/** Everything under `aiChangeKeys.all`, so a revert invalidates the grid, the pages it
 *  touched and every open plan in one call. */
export const aiChangeKeys = {
  all: ['aiChanges'] as const,
  list: ['aiChanges', 'list'] as const,
  batch: (batchId: string) => ['aiChanges', 'batch', batchId] as const,
  detail: (id: string) => ['aiChanges', 'detail', id] as const,
  entries: (id: string) => ['aiChanges', 'entries', id] as const,
  entry: (id: string, seq: number) => ['aiChanges', 'entry', id, seq] as const,
  history: (id: string) => ['aiChanges', 'history', id] as const,
  revertOutcome: (id: string, taskId: string) =>
    ['aiChanges', 'revertOutcome', id, taskId] as const,
  revertPlan: (id: string, d: RevertDecisions) =>
    [
      'aiChanges',
      'revertPlan',
      id,
      normalizeSkips(d.skips).join(','),
      d.force,
      d.confirmSoftRefs,
    ] as const,
};

// ---- words -----------------------------------------------------------------------

type ChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';

export const AI_CHANGE_CHANNELS: readonly { value: AIChangeChannel; label: string }[] = [
  { value: 'mcp', label: 'MCP' },
  { value: 'chat', label: 'Console agent' },
  { value: 'design_import', label: 'Design import' },
  { value: 'translation_sync', label: 'Translation sync' },
  { value: 'revert', label: 'Revert' },
];

export const AI_CHANGE_STATUSES: readonly {
  value: AIChangeStatus;
  label: string;
  color: ChipColor;
}[] = [
  { value: 'applying', label: 'Applying', color: 'info' },
  { value: 'applied', label: 'Applied', color: 'success' },
  { value: 'failed', label: 'Failed', color: 'error' },
  { value: 'reverting', label: 'Reverting', color: 'info' },
  { value: 'reverted', label: 'Reverted', color: 'default' },
  { value: 'partially_reverted', label: 'Partially reverted', color: 'warning' },
];

export function channelLabel(channel: string): string {
  return AI_CHANGE_CHANNELS.find((c) => c.value === channel)?.label ?? channel;
}

export function statusLabel(status: string): string {
  return AI_CHANGE_STATUSES.find((s) => s.value === status)?.label ?? status;
}

export function statusColor(status: string): ChipColor {
  return AI_CHANGE_STATUSES.find((s) => s.value === status)?.color ?? 'default';
}

export const ACTOR_KIND_LABELS: Record<AIChangeListItem['actor_kind'], string> = {
  ops: 'Ops',
  tenant: 'Tenant user',
  system: 'System',
};

export const CREDENTIAL_KIND_LABELS: Record<AIChangeListItem['credential_kind'], string> = {
  oauth: 'MCP sign-in (OAuth)',
  console_session: 'Console session',
  dashboard_session: 'Dashboard session',
  // A client's API key on the content proposals API (sandbox applies; WPS P3-17).
  api_key: 'API key (content proposals API)',
  system: 'System',
};

/** The first eight characters of a change-set id: enough to tell two apart on one screen. */
export function shortId(id: string): string {
  return id.slice(0, 8);
}

/** What reverting does to one record. */
export const REVERT_ACTION_LABELS: Record<AIChangeRevertEntry['action'], string> = {
  restore: 'Restore',
  recreate: 'Re-create',
  delete: 'Delete',
  disable: 'Disable',
  keep: 'Keep',
  noop: 'Nothing to do',
};

/** The plan's `summary` keys, as "3 restored · 1 skipped". */
export const PLAN_SUMMARY_WORDS: Record<string, string> = {
  restore: 'restored',
  recreate: 're-created',
  delete: 'deleted',
  disable: 'disabled',
  keep: 'kept',
  noop: 'unchanged',
  skip: 'skipped',
};

/** How an issue gets out of the way: `force` (only an Ops Admin may overwrite a conflict),
 *  `skip` (leave that record as it is), `confirm` (the soft-reference checkbox), or `info`
 *  (nothing to decide). The plan's `blockers` stay the authority on what still blocks — these
 *  only choose the words and the tone. */
export type RevertIssueKind = 'force' | 'skip' | 'confirm' | 'info';

const REVERT_ISSUES: Record<string, { label: string; kind: RevertIssueKind }> = {
  conflict: { label: 'Changed since', kind: 'force' },
  fk_missing_protect: { label: 'Missing reference', kind: 'skip' },
  fk_missing_cascade: { label: 'Missing parent', kind: 'skip' },
  referenced_from_outside: { label: 'Used elsewhere', kind: 'skip' },
  unique_collision: { label: 'Duplicate value', kind: 'skip' },
  snapshot_missing: { label: 'Snapshot missing', kind: 'skip' },
  not_tenant_row: { label: 'Not this tenant’s', kind: 'skip' },
  soft_ref_warning: { label: 'Soft reference', kind: 'confirm' },
  will_disable: { label: 'Disabled, not deleted', kind: 'info' },
  feed_rewritten: { label: 'Feed rewrites it', kind: 'info' },
  field_dropped: { label: 'Field dropped', kind: 'info' },
  fk_set_null: { label: 'Reference cleared', kind: 'info' },
  m2m_link_dropped: { label: 'Link dropped', kind: 'info' },
  edited_since: { label: 'Edited since', kind: 'info' },
  feed_owned: { label: 'Feed-owned', kind: 'info' },
};

export function revertIssue(code: string): { label: string; kind: RevertIssueKind } {
  return REVERT_ISSUES[code] ?? { label: code.replace(/_/g, ' '), kind: 'info' };
}

/** What a revert did to one entry (the entries list, after a revert ran). */
export const ENTRY_REVERT_STATUS_LABELS: Record<
  NonNullable<AIChangeEntry['revert_status']>,
  string
> = {
  restored: 'Restored',
  skipped: 'Skipped',
  disabled: 'Disabled',
  conflict: 'Conflict',
};
